import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import Card from '../../common/Card';
import Button from '../../common/Button';
import FormSelect from '../../common/FormSelect';
import FormCheckbox from '../../common/FormCheckbox';
import Loading from '../../common/Loading';
import ErrorMessage from '../../common/ErrorMessage';
import './styles.css';

/**
 * 客戶偏好設定編輯組件
 * 用於編輯客戶的偏好設定，如性別和接收相關資訊的選項
 * 
 * @param {Object} props - 組件屬性
 * @param {string} props.token - 認證令牌
 * @param {Object} props.user - 用戶信息
 * @returns {JSX.Element} 客戶偏好設定編輯組件
 */
function CustomerMetafieldsEdit({ token, user }) {
    const { customerId } = useParams();
    const navigate = useNavigate();

    // 本地狀態
    const [customer, setCustomer] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [formData, setFormData] = useState({
        gender: '',
        receiveRelatedInfo: false
    });
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState(null);

    // 加載客戶數據
    useEffect(() => {
        const fetchCustomerDetails = async () => {
            try {
                setLoading(true);
                // 從所有客戶中獲取特定客戶
                const response = await axios.get('/api/customers', {
                    headers: { Authorization: `Bearer ${token}` }
                });

                // 找到匹配的客戶
                const foundCustomer = response.data.find(c =>
                    c.id.split('/').pop() === customerId || c.id === customerId
                );

                if (foundCustomer) {
                    setCustomer(foundCustomer);
                    // 初始化表單數據
                    setFormData({
                        gender: foundCustomer.gender || '',
                        receiveRelatedInfo: foundCustomer.receiveRelatedInfo || false
                    });
                } else {
                    setError('找不到客戶資料');
                }
            } catch (error) {
                console.error('Error fetching customer details:', error);
                setError('獲取客戶資料時出錯');
            } finally {
                setLoading(false);
            }
        };

        if (customerId) {
            fetchCustomerDetails();
        }
    }, [customerId, token]);

    // 處理表單輸入變化
    const handleInputChange = (name, value) => {
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // 處理表單提交
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!customer) return;

        try {
            setSaving(true);
            setSaveError(null);

            // 發送更新請求
            const response = await axios.put('/api/customers/update-metafields', {
                customerId: customer.id,
                gender: formData.gender,
                receiveRelatedInfo: formData.receiveRelatedInfo
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });

            // 檢查是否部分成功
            if (response.data.partialSuccess) {
                console.warn('部分更新成功:', response.data);
                alert('部分更新成功，部分失敗。');
            }

            // 更新成功，導航到詳情頁面
            navigate(`/customers/${customerId}`);
        } catch (error) {
            console.error('更新客戶偏好設定時出錯:', error);

            let errorMessage = '更新客戶資料時出錯';

            // 提取更詳細的錯誤信息
            if (error.response) {
                if (error.response.data && error.response.data.error) {
                    if (typeof error.response.data.error === 'string') {
                        errorMessage = error.response.data.error;
                    } else if (Array.isArray(error.response.data.error)) {
                        errorMessage = error.response.data.error.map(err => err.message).join(', ');
                    } else if (error.response.data.error.message) {
                        errorMessage = error.response.data.error.message;
                    }
                }

                // 檢查是否有詳細的錯誤信息
                if (error.response.data && error.response.data.errors && Array.isArray(error.response.data.errors)) {
                    const errorDetails = error.response.data.errors.map(err =>
                        `${err.field}: ${err.error}`
                    ).join('\n');
                    errorMessage = `更新失敗:\n${errorDetails}`;
                }
            }

            setSaveError(errorMessage);
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return <Loading text="加載中..." />;
    }

    if (error) {
        return (
            <div className="customer-metafields-container">
                <ErrorMessage message={error} />
                <Button variant="outline" onClick={() => navigate('/')}>返回客戶列表</Button>
            </div>
        );
    }

    if (!customer) {
        return (
            <div className="customer-metafields-container">
                <ErrorMessage message="找不到客戶資料" />
                <Button variant="outline" onClick={() => navigate('/')}>返回客戶列表</Button>
            </div>
        );
    }

    return (
        <div className="customer-metafields-container">
            <div className="customer-metafields-header">
                <Button
                    variant="outline"
                    size="small"
                    onClick={() => navigate(`/customers/${customerId}`)}
                    className="customer-metafields-back-button"
                >
                    返回詳情
                </Button>
                <h2 className="customer-metafields-title">編輯客戶偏好設定</h2>
            </div>

            <Card className="customer-metafields-card">
                <form onSubmit={handleSubmit} className="customer-metafields-form">
                    <div className="form-group">
                        <FormSelect
                            label="性別"
                            name="gender"
                            value={formData.gender}
                            onChange={(e) => handleInputChange('gender', e.target.value)}
                            options={[
                                { value: '', label: '請選擇' },
                                { value: 'male', label: '男性' },
                                { value: 'female', label: '女性' },
                                { value: 'other', label: '其他' }
                            ]}
                        />
                    </div>

                    <div className="form-group">
                        <FormCheckbox
                            label="接收相關資訊和優惠"
                            name="receiveRelatedInfo"
                            checked={formData.receiveRelatedInfo}
                            onChange={(e) => handleInputChange('receiveRelatedInfo', e.target.checked)}
                            hint="勾選此選項表示您同意接收我們的最新消息、優惠和相關資訊。"
                        />
                    </div>

                    {saveError && <ErrorMessage message={saveError} />}

                    <div className="customer-metafields-actions">
                        <Button
                            variant="outline"
                            type="button"
                            onClick={() => navigate(`/customers/${customerId}`)}
                        >
                            取消
                        </Button>
                        <Button
                            variant="primary"
                            type="submit"
                            disabled={saving}
                        >
                            {saving ? '保存中...' : '保存更改'}
                        </Button>
                    </div>
                </form>
            </Card>
        </div>
    );
}

export default CustomerMetafieldsEdit; 